import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { ensureFloorIsolationPropertyRegistered, FLOOR_ISOLATION_TOGGLE_OBJECT_ID } from '@pointorama/potree-shared';
import FilterIcon from '../../../../assets/icons/filter.svg?react';
import { Project } from '../../../../types/graphqlTypes';
import { AnnotationContextGroup } from '../../../../contexts/AnnotationContext';
import { RendererContext } from '../../../../contexts/RendererContext';
import { useReactiveProperty } from '../../../Properties/reactive/useReactiveProperty';
import { PropertyType } from '../../../Properties/types';

const FloorIsolationToggle_: React.FC2<{
  project?: Pick<Project, 'id'>;
  group: AnnotationContextGroup;
}> = ({ project, group }) => {
  const { viewer } = useContext(RendererContext);
  const floorGroupId = group.identifier;
  ensureFloorIsolationPropertyRegistered(floorGroupId);

  const [isIsolationEnabled, setIsIsolationEnabled] = useReactiveProperty({
    projectId: project?.id || '',
    identifier: `floor-isolation.${floorGroupId}`,
    objectIdentifier: FLOOR_ISOLATION_TOGGLE_OBJECT_ID,
    propertyType: PropertyType.PROJECT_USER,
  });

  const [uiVisible, setUiVisible] = useState(viewer?.floorIsolationManager.uiVisible$.value ?? true);
  useEffect(() => {
    if (!viewer) return;
    const sub = viewer.floorIsolationManager.uiVisible$.subscribe(setUiVisible);
    return () => sub.unsubscribe();
  }, [viewer]);

  const onToggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsIsolationEnabled(!isIsolationEnabled);
    },
    [isIsolationEnabled, setIsIsolationEnabled],
  );

  if (!uiVisible) return null;

  return (
    <div onClick={onToggle} className="flex items-center justify-center w-6 h-6 cursor-pointer relative" title="Isolate floor view">
      <FilterIcon className={`w-4 h-4 stroke-2 ${isIsolationEnabled ? 'text-green-500 hover:text-green-600' : 'text-gray-700 hover:text-black dark:hover:text-neon-green-300'}`} />
    </div>
  );
};

export const FloorIsolationToggle = memo(FloorIsolationToggle_);
